import { z } from 'zod';
import { NumericSchema, Unit4EnvSchema } from './common.js';
import { SizingInputSchema } from './sizing.js';
import { NewArticleSchema } from './supplier.js';

export const ProductOriginSchema = z.enum(['manual', 'import', 'unit4']);
export type ProductOrigin = z.infer<typeof ProductOriginSchema>;

/**
 * Productstatus — altijd afgeleid door de backend (`recomputeStatus`), nooit handmatig gezet.
 * draft → incomplete (≥1 regel provisional/invalid) → ready (valide, nog nooit gepusht)
 * synced (lokaal == snapshot) / modified (lokaal ≠ snapshot) / conflict (Unit4 ≠ snapshot) / sync_error
 */
export const ProductStatusSchema = z.enum([
  'draft',
  'incomplete',
  'ready',
  'synced',
  'modified',
  'conflict',
  'sync_error',
]);
export type ProductStatus = z.infer<typeof ProductStatusSchema>;

export const LineStatusSchema = z.enum(['linked', 'provisional', 'invalid']);
export type LineStatus = z.infer<typeof LineStatusSchema>;

/** Kandidaat uit de ai-parser (`ask_smart`), verrijkt met Unit4-info na koppelen. */
export const AiCandidateSchema = z.object({
  artikelnummer: z.string(),
  label: z.string().nullable().optional(),
  confidence: z.number().nullable().optional(),
  why: z.string().nullable().optional(),
  source_pdf: z
    .object({
      filename: z.string().nullable().optional(),
      page: z.number().nullable().optional(),
      snippet: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  desc_nl: z.string().nullable().optional(),
  desc_en: z.string().nullable().optional(),
});
export type AiCandidate = z.infer<typeof AiCandidateSchema>;

export const ValidationIssueSchema = z.object({
  severity: z.enum(['error', 'warning']),
  code: z.string(),
  message: z.string(),
  productId: z.number().int(),
  lineId: z.number().int().nullable().optional(),
  field: z.string().nullable().optional(),
  /** Alleen bij DESCRIPTION_MISMATCH: beide kanten, zodat de gebruiker kiest welke klopt. */
  mismatch: z
    .object({
      description: z.string(),
      unit4_description: z.string(),
      unit4_product_code: z.string(),
      reason: z.string(),
    })
    .optional(),
});
export type ValidationIssue = z.infer<typeof ValidationIssueSchema>;

export const ProductLineSchema = z.object({
  id: z.number().int(),
  product_id: z.number().int(),
  sort_order: z.number().int(),
  line_number: z.number().int().nullable(),
  unit4_composition_id: z.string().nullable(),
  description: z.string(),
  unit4_product_code: z.string().nullable(),
  unit4_description: z.string().nullable(),
  unit_code: z.string().nullable(),
  qty_in_composition: NumericSchema,
  composition_per: NumericSchema,
  percentage_loss: NumericSchema,
  child_product_id: z.number().int().nullable(),
  is_composite: z.boolean(),
  /** Nieuw artikel: nog niet in Unit4, wordt bij goedkeuring aangemaakt (BugHerd #1468) */
  new_article: NewArticleSchema.nullable().optional(),
  status: LineStatusSchema,
  ai_query: z.string().nullable(),
  ai_candidates: z.array(AiCandidateSchema).nullable(),
  validation_errors: z.array(ValidationIssueSchema).nullable(),
  notes: z.string().nullable(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  /** Alleen gevuld in samengestelde responses (naam/status van het sub-product). */
  child_product: z
    .object({
      id: z.number().int(),
      name: z.string(),
      status: ProductStatusSchema,
      unit4_product_code: z.string().nullable(),
      proposed_product_code: z.string().nullable(),
    })
    .nullable()
    .optional(),
});
export type ProductLine = z.infer<typeof ProductLineSchema>;

/**
 * Beoordelingsstatus (los van de afgeleide productstatus):
 * NULL → nooit ingediend; submitted → wacht op een goedkeurder; rejected → afgekeurd met opmerking;
 * approved → door een goedkeurder in Unit4 opgeslagen.
 */
/** Boolean uit een query-string: "false", "0" en leeg zijn écht false (z.coerce.boolean maakt van elke niet-lege string true). */
export const QueryBoolSchema = z.preprocess((v) => v === true || v === 'true' || v === '1', z.boolean());

export const ReviewStatusSchema = z.enum(['submitted', 'rejected', 'approved']);
export type ReviewStatus = z.infer<typeof ReviewStatusSchema>;

export const ProductSchema = z.object({
  id: z.number().int(),
  owner_id: z.number().int(),
  owner_name: z.string().nullable().optional(),
  owner_email: z.string().nullable().optional(),
  name: z.string(),
  /** Lange productomschrijving (Unit4 productLanguageNotes N/E) */
  long_description_nl: z.string().nullable().optional().default(null),
  long_description_en: z.string().nullable().optional().default(null),
  /** Werkplaatsinstructies voor assemblage (Unit4 products.assemblyInstruction) */
  assembly_instruction: z.string().nullable().optional().default(null),
  /** Assemblage-instellingen (Unit4-velden op het productrecord); NULL = waarde van het sjabloon volgen */
  assembly_warehouse_code: z.string().nullable().optional().default(null),
  use_partial_relocation: z.enum(['J', 'N']).nullable().optional().default(null),
  use_component_partial: z.enum(['J', 'N']).nullable().optional().default(null),
  partial_relocation_minimum: z.coerce.number().nullable().optional().default(null),
  apply_sub_assembly_end_product: z.enum(['J', 'N']).nullable().optional().default(null),
  apply_sub_assembly_component: z.enum(['J', 'N']).nullable().optional().default(null),
  /** Overrides van sjabloonvelden op het productrecord (Unit4 referenceJournalEntryCode / warehouseCode / locationCode / eanCode / costPrice); NULL = sjabloon volgen */
  journal_entry_code: z.string().nullable().optional().default(null),
  warehouse_code: z.string().nullable().optional().default(null),
  location_code: z.string().nullable().optional().default(null),
  ean_code: z.string().nullable().optional().default(null),
  cost_price: z.coerce.number().nullable().optional().default(null),
  search_name: z.string().nullable(),
  proposed_product_code: z.string().nullable(),
  unit_code: z.string(),
  template_product_code: z.string().nullable(),
  /** FK naar Klingerator-sjabloon (product_templates); wint van template_product_code */
  template_id: z.number().int().nullable().optional().default(null),
  /** Naam van het gekoppelde sjabloon (alleen-lezen, join) */
  template_name: z.string().nullable().optional(),
  origin: ProductOriginSchema,
  status: ProductStatusSchema,
  unit4_product_code: z.string().nullable(),
  unit4_env: Unit4EnvSchema.nullable(),
  snapshot_hash: z.string().nullable(),
  loaded_at: z.string().nullable(),
  last_pushed_at: z.string().nullable(),
  version: z.number().int(),
  validation_errors: z.array(ValidationIssueSchema).nullable(),
  notes: z.string().nullable(),
  /** Opt-in actuator-sizing-invoer (products.sizing_input JSONB); NULL = geen sizing op dit product */
  sizing_input: SizingInputSchema.nullable().optional().default(null),
  review_status: ReviewStatusSchema.nullable().optional(),
  /** true als het product gearchiveerd (soft-deleted) is — alleen-lezen */
  archived: z.boolean().optional(),
  /**
   * Plaatsing: true = staat expliciet in de productenlijst (root). Ouders zijn de composite-regels
   * die naar dit product verwijzen (meerdere mogelijk); zonder ouder verschijnt een product altijd
   * in de lijst, ongeacht deze vlag.
   */
  is_root: z.boolean().optional().default(true),
  /** Aantal (actieve) producten waarin dit product als sub-product hangt — alleen-lezen */
  parent_count: z.number().int().optional(),
  submitted_at: z.string().nullable().optional(),
  reviewed_at: z.string().nullable().optional(),
  reviewed_by: z.number().int().nullable().optional(),
  reviewer_name: z.string().nullable().optional(),
  review_comment: z.string().nullable().optional(),
  line_count: z.number().int().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});
export type Product = z.infer<typeof ProductSchema>;

export const ProductWithLinesSchema = ProductSchema.extend({ lines: z.array(ProductLineSchema) });
export type ProductWithLines = z.infer<typeof ProductWithLinesSchema>;

// ---------- Request bodies ----------

export const CreateProductBodySchema = z.object({
  name: z.string().trim().min(1, 'Naam is verplicht').max(200),
  search_name: z.string().trim().max(50).optional().nullable(),
  unit_code: z.string().trim().min(1).max(10).optional(),
  template_product_code: z.string().trim().max(50).optional().nullable(),
  template_id: z.number().int().positive().optional().nullable(),
  proposed_product_code: z.string().trim().max(50).optional().nullable(),
  long_description_nl: z.string().trim().max(4000).optional().nullable(),
  long_description_en: z.string().trim().max(4000).optional().nullable(),
  /** Instructiedeel + (door de backend samengevoegde) lange omschrijving — daarom ruimer dan 8000 */
  assembly_instruction: z.string().trim().max(12000).optional().nullable(),
  assembly_warehouse_code: z.string().trim().max(10).optional().nullable(),
  use_partial_relocation: z.enum(['J', 'N']).optional().nullable(),
  use_component_partial: z.enum(['J', 'N']).optional().nullable(),
  partial_relocation_minimum: z.number().min(0).optional().nullable(),
  apply_sub_assembly_end_product: z.enum(['J', 'N']).optional().nullable(),
  apply_sub_assembly_component: z.enum(['J', 'N']).optional().nullable(),
  journal_entry_code: z.string().trim().max(10).optional().nullable(),
  warehouse_code: z.string().trim().max(10).optional().nullable(),
  location_code: z.string().trim().max(20).optional().nullable(),
  ean_code: z.string().trim().max(20).optional().nullable(),
  cost_price: z.number().min(0).optional().nullable(),
  notes: z.string().max(5000).optional().nullable(),
  /** Opt-in actuator-sizing-invoer; NULL wist het blok */
  sizing_input: SizingInputSchema.nullable().optional(),
});
export type CreateProductBody = z.infer<typeof CreateProductBodySchema>;

export const UpdateProductBodySchema = CreateProductBodySchema.partial().extend({
  version: z.number().int(),
});
export type UpdateProductBody = z.infer<typeof UpdateProductBodySchema>;

export const LineInputSchema = z.object({
  id: z.number().int().optional().nullable(),
  sort_order: z.number().int().min(1),
  description: z.string().trim().min(1, 'Omschrijving is verplicht').max(500),
  unit4_product_code: z.string().trim().max(50).nullable().optional(),
  unit_code: z.string().trim().max(10).nullable().optional(),
  qty_in_composition: z.coerce.number().positive('Aantal moet groter dan 0 zijn'),
  composition_per: z.coerce.number().positive('Per moet groter dan 0 zijn').default(1),
  percentage_loss: z.coerce.number().min(0).max(99.9999).default(0),
  child_product_id: z.number().int().nullable().optional(),
  new_article: NewArticleSchema.nullable().optional(),
  ai_candidates: z.array(AiCandidateSchema).nullable().optional(),
  ai_query: z.string().nullable().optional(),
  notes: z.string().max(5000).nullable().optional(),
});
export type LineInput = z.infer<typeof LineInputSchema>;

export const ReplaceLinesBodySchema = z.object({
  version: z.number().int(),
  lines: z.array(LineInputSchema),
});
export type ReplaceLinesBody = z.infer<typeof ReplaceLinesBodySchema>;

export const LinkLineBodySchema = z.object({
  unit4_product_code: z.string().trim().min(1).max(50),
  unit_code: z.string().trim().max(10).optional().nullable(),
});
export type LinkLineBody = z.infer<typeof LinkLineBodySchema>;

/** Product in/uit de productenlijst zetten (root-plaatsing); ouders blijven ongewijzigd. */
export const SetRootBodySchema = z.object({ is_root: z.boolean() });
export type SetRootBody = z.infer<typeof SetRootBodySchema>;

export const MakeCompositeBodySchema = z.union([
  z.object({ name: z.string().trim().min(1).max(200) }),
  z.object({ child_product_id: z.number().int() }),
]);
export type MakeCompositeBody = z.infer<typeof MakeCompositeBodySchema>;

export const ProductListQuerySchema = z.object({
  scope: z.enum(['mine', 'all']).default('mine'),
  status: ProductStatusSchema.optional(),
  origin: ProductOriginSchema.optional(),
  /**
   * Standaard toont de lijst alleen root-producten (is_root) plus producten zonder ouder;
   * true = ook geneste sub-producten tonen.
   */
  include_nested: QueryBoolSchema.optional(),
  /** true = alleen gearchiveerde (verwijderde) producten tonen */
  archived: QueryBoolSchema.optional(),
  q: z.string().trim().max(200).optional(),
  sort: z.enum(['updated_at', 'created_at', 'name', 'status', 'proposed_product_code']).default('updated_at'),
  order: z.enum(['asc', 'desc']).default('desc'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ProductListQuery = z.infer<typeof ProductListQuerySchema>;

/** Lijst voor goedkeurders: standaard alleen open inzendingen, meest recente eerst. */
export const ReviewListQuerySchema = z.object({
  /** true = ook afgehandelde (approved/rejected) tonen */
  handled: QueryBoolSchema.default(false),
  q: z.string().trim().max(200).optional(),
  sort: z.enum(['submitted_at', 'name', 'owner', 'review_status']).default('submitted_at'),
  order: z.enum(['asc', 'desc']).default('desc'),
  limit: z.coerce.number().int().min(1).max(100).default(30),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ReviewListQuery = z.infer<typeof ReviewListQuerySchema>;

export const RejectReviewBodySchema = z.object({
  comment: z.string().trim().min(3, 'Een opmerking voor de indiener is verplicht bij afkeuren').max(2000),
});
export type RejectReviewBody = z.infer<typeof RejectReviewBodySchema>;
